import { MutableRefObject } from 'react'
import { Provider } from './domain'
import { Validation, ValueApi, valueApi, valueIndex } from './value'
import { personNameWithDegree } from './format'
import { providerSearch } from './provider-search-requests'

export interface ControlProps<T = any, U = any> extends Validation<T> {
  id?: string
  name?: string
  label?: string

  disabled?: boolean
  selected?: boolean
  required?: boolean

  togglePassword?: boolean

  min?: number
  max?: number
  step?: number
  value?: any

  minDate?: any
  maxDate?: any
  removeTime?: boolean

  tooltip?: string
  placeholder?: string

  noEmptyOption?: boolean
  options?: Option[]

  multipleSelect?: boolean

  message?: string

  style?: any
  variant?: 'filled' | 'outlined'
  row?: boolean

  multiline?: boolean
  rows?: number

  startIcon?: string
  inputRef?: MutableRefObject<HTMLElement | HTMLInputElement>

  autoFocus?: boolean

  helperText?: string
  clearIcon?: boolean
  disableClearable?: boolean

  searchWidth?: number | string
  searchHeight?: number | string
  optionWidth?: number | string
  optionHeight?: number | string
  small?: boolean
  tabIndex?: number
  labelPlacement?: any
  changeOnEnter?: boolean
  useBooleanValue?: boolean
  inputProps?: any

  icon?: (item?: any) => string
  onEnter?: (value?: U) => void
  onEnterValid?: (value?: U) => void
  onKeyDown?: (value?: U) => void
  onKeyUp?: (value?: U) => void
  onClear?: () => void
  onClose?: () => void
  equals?: (a: any, b: any) => boolean
  optionDisabled?: (a: any) => boolean
}

export interface Option<T = any, U = any> {
  label?: string
  value?: T
  source?: U
  disabled?: boolean
  hidden?: boolean
  selected?: boolean
  style?: any
}

interface Options<T = any, U = any> extends ControlProps<T, U> {
  list?: T[]
  option?: (value: T) => Option<T, U>
  noEmptyOption?: boolean
}

export interface ListProps<T = any, U = any> extends ControlProps<T, U> {
  list?: U[]
  asyncList?: (search: string) => Promise<U[]>
  textLabel?: (value: U) => string
  listLabel?: (value: U) => string
  listValue?: (value: U) => any
  onInputChange?: (text: string, e?: any) => void
}

type Field<T = any, U = any> = ControlProps<T> | ListProps<T, U> | Options<T, U>

export interface Control<T = any, U = any> {
  field: Field<T, U>

  value: ValueApi<T>
  disabled: ValueApi<boolean>
  selected: ValueApi<boolean>

  list?: ValueApi<T[]>
  options?: ValueApi<Option[]>
  index?: ValueApi<number>

  inputRef?: MutableRefObject<HTMLElement | HTMLInputElement>
  open?: ValueApi<boolean>

  id?: string

  valid(): boolean
  clear(value?: any): void
  setValue?(value: T): void
  setListValue?(match: any): any | null
  variant?: 'filled' | 'standard' | 'outlined'
}

export function control<T = any, U = any>(field: Field<T, U>): Control<T, U> {
  let value: ValueApi<T>

  const { option, list, listLabel, inputRef, equals, onChange, required } = field as any

  if (required && !field.valid) {
    field.valid = nullValidator(field.label)
  }

  let index: ValueApi<number>
  let listApi: ValueApi<T[]>
  let optionApi: ValueApi<Option[]>

  if (list && option) {
    field.options = option
      ? list.map((source) => ({
          ...{ source, value: source },
          ...option(source),
        }))
      : list.map((source) => ({
          ...{ source, value: source, label: source.toString() },
        }))

    const indexApi = valueIndex(field as Options<T, U> as any)
    value = indexApi.value
    index = indexApi.index
  } else if (list && listLabel) {
    value = valueApi(field as Validation)
    listApi = valueApi({ defaultValue: list })
  } else {
    value = valueApi(field as Validation)
  }

  if (field.options) {
    optionApi = valueApi({ defaultValue: field.options })
  }

  let setListValue: (match: any) => void
  if (list) {
    setListValue = (v) => {
      let match: any = null
      const values = listApi.getState().value

      if (typeof v === 'function') {
        match = values.filter(v)[0]
      } else {
        const fn = equals || ((a, b) => a === b)
        match = values.filter((a) => fn(a, v))[0]
      }
      if (onChange) {
        onChange(match)
      }

      value.getState().setValue(match)

      return match
    }
  }
  const valid = () => {
    if (field.valid) {
      return !field.valid(value.getState().value)
    }
    return !!value.getState().value
  }

  const clear = (v?: any) => {
    value.setState({ value: v || undefined, error: undefined })
    if (listApi) {
      listApi.getState().setValue([])
    }
  }

  const setValue = (v?: any) => {
    value.setState({ value: v })
    if (field._onChange) {
      field._onChange(v)
    }
  }

  const disabled = valueApi({ defaultValue: field.disabled || false })
  const selected = valueApi({ defaultValue: field.selected || false })

  return {
    field,
    value,
    disabled,
    selected,
    index,
    inputRef,
    valid,
    setValue,
    setListValue,
    clear,
    list: listApi,
    options: optionApi,
  }
}

const emptyStringValidator = (name: string) => (v: string) => (v || '').length ? undefined : `${name} is required`

export function stringControl(field: ControlProps<string>) {
  const f: ControlProps<string> = {
    ...{
      defaultValue: '',
      sanitize: null,
      valid: field.required ? emptyStringValidator(field.name) : null,
    },
    ...field,
  }
  return control(f)
}

interface NumberControlArgs extends ControlProps<number> {
  min: number
  max: number
}

const nanValidator = (name: string) => (v: number) => !isNaN(v) ? undefined : `${name} is required`
export function numberControl(field: NumberControlArgs) {
  const f = {
    ...{
      defaultValue: NaN,
      valid: field.required ? nanValidator(field.name) : null,
    },
    ...field,
  }
  return control(f as any)
}

function nullValidator(name: string) {
  return (v: any) => (v ? undefined : `${name} is required`)
}

export function objectControl(field: ControlProps<object>) {
  const f: ControlProps<object> = {
    ...{
      valid: field.required ? nullValidator(field.name) : null,
    },
    ...field,
  }
  return control(f)
}

type Fields<T, U = any> = { [key in keyof T]: Partial<Field<any, U>> }

export interface FormProps<T = any, U = any> {
  values: T
  fields?: Fields<T, U>
  resetOnSubmit?: boolean
  disabled?: boolean
  onChange?: (values: T) => void
  valid?: (values: T) => boolean
  onValid?: (values: T) => void
  onSubmit?: (values: T) => void
  resetValues?: () => T
}

interface Values<T = any> {
  [key: string]: T
}
type Controls<T> = { [key in keyof T]: Control }

export interface FormApi<T = any> {
  values: ValueApi<T>
  valid: ValueApi<boolean>
  controls: Controls<T>
  numFields: number
  setValues(v: Partial<T>, clearErrors?: boolean): void
  disableControls(value: boolean): void
  submit(): void
  reset?: () => void
}

export function formApi<T = any>(p: FormProps<T>): FormApi<T> {
  const props = { ...{ resetOnSubmit: true }, ...p }
  const fields = props.fields || {}
  const values = valueApi({ defaultValue: props.values })
  const controls: Partial<Controls<T>> = {}
  const valid = valueApi({ defaultValue: false })

  function updateControlValues(newValues: any): void {
    for (const [name, value] of Object.entries(newValues)) {
      if (controls[name]) {
        controls[name].value.getState().setValue(value)
      }
    }
  }

  function resetControlValues(newValues: any, error = undefined) {
    for (const [name, value] of Object.entries(newValues)) {
      if (controls[name]) {
        controls[name].value.setState({ value, error })
      }
    }
  }
  function disableControls(value: boolean) {
    for (const name of Object.keys(controls)) {
      const ctl: Control = controls[name]
      ctl.disabled.setState({ value })
    }
  }

  function clearControlErrors() {
    for (const name of Object.keys(controls)) {
      const ctl: Control = controls[name]
      ctl.value.setState({ error: undefined })
    }
  }

  function controlValues() {
    const v = {}
    for (const name of Object.keys(controls)) {
      const ctl: Control = controls[name]
      const value = ctl.value.getState().value || values[name]
      v[name] = value
    }
    return v
  }

  const valuesUpdate = async (keyValue: Values, submit?: any, clearErrors?: boolean) => {
    const newValues: T = { ...values.getState().value, ...keyValue } as any

    if (submit) {
      for (const name of Object.keys(controls)) {
        const ctl: Control = controls[name]

        const value = ctl.value.getState().value || values[name]
        ctl.value.getState().setValue(value)

        newValues[name] = value
        // console.log('new', name, value, ctl.value.getState().value)
      }
    } else {
      updateControlValues(newValues)
    }

    values.getState().setValue(newValues)

    if (submit) {
      valid.setState({ value: false })

      const validator = props.valid
      if (validator) {
        const success = validator(newValues)
        if (success) {
          if (props.onValid) {
            props.onValid(newValues)
          }
          await submit(newValues)
          // console.log('   valid.setState', true)
          valid.setState({ value: true })
          if (props.resetValues) {
            const resetValues = props.resetValues()
            // console.log('reset', resetValues)
            resetControlValues(resetValues, undefined)
          } else {
            clearControlErrors()
          }
        }
      } else {
        submit(newValues)
      }
    }

    if (props.onChange) {
      props.onChange(newValues)
    }

    if (clearErrors) {
      setTimeout(() => {
        clearControlErrors()
      })
    }
  }
  let numFields = 0

  for (const [name, value] of Object.entries(props.values)) {
    const field: Partial<Field> = fields[name]
    field.name = name
    field.defaultValue = field.defaultValue || value

    const { onChange } = field
    field._onChange = onChange
    field.onChange = (v: any) => {
      // console.log('  field.onChange ', name, v, onChange)

      if (controls[name].value.getState().value !== v) {
        const patch = {}
        patch[name] = v

        if (onChange) {
          onChange(v)
        }

        const newValues = { ...values.getState().value, ...patch }
        values.getState().setValue(newValues)

        if (props.onChange) {
          props.onChange(newValues)
        }
      }
    }

    let ctl: Control

    field.disabled = field.disabled || p.disabled

    switch (typeof field.defaultValue) {
      case 'string':
        ctl = field.options || (field as ListProps).list ? objectControl(field) : stringControl(field)
        break
      case 'number':
        ctl = numberControl(field as any)
        break
      default:
        ctl = objectControl(field)
    }

    controls[name] = ctl
    numFields++
  }

  return {
    values,
    valid,
    controls: controls as Controls<T>,
    numFields,
    disableControls,
    setValues: (v, clearErrors) => valuesUpdate(v, null, clearErrors),
    submit: () => valuesUpdate(controlValues(), props.onSubmit),
  }
}

export function providerField(list: Provider[], props?: ControlProps): ListProps<Provider> {
  return {
    ...{
      list,
      listLabel: personNameWithDegree,
      label: 'Physician',
      required: true,
      searchWidth: '220px',
      equals: (a: Provider, b: Provider) => a?.providerId === b?.providerId,
    },
    ...props,
  }
}


export function asyncProviderField(
  locationCode: string,
  onChange: (a: Provider) => void,
  props?: ControlProps,
  departmentCode?: string
): ListProps<Provider> {
  return {
    ...{
      asyncList: providerSearch(locationCode, departmentCode),
      listLabel: personNameWithDegree,
      label: 'Physician',
      required: true,
      searchWidth: '220px',
      onChange: (a: Provider) => a && onChange(a),
      equals: (a: Provider, b: Provider) => a?.providerId === b?.providerId,
    },
    ...props,
  }
}